import { getFormImageUrl } from '@cineamo/api/models/website-configuration/form-image-helper';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { FormContactKey } from './form-contact-helper';

const getFormConfig = (domainConfiguration: DomainConfigurationDto, formKey: FormContactKey) =>
    domainConfiguration.cinema?._embedded?.cinemaConfiguration?.websiteConfiguration?.forms?.[formKey];

/**
 * Resolves the hero image for an OUR SERVICES form page: the form's own uploaded image if set,
 * otherwise the `hallImageUrl` passed in from the page (the cinema's first cinema room picture,
 * if any), otherwise `undefined` so callers keep their own default image.
 */
export function resolveFormImageUrl(
    domainConfiguration: DomainConfigurationDto,
    formKey: FormContactKey,
    hallImageUrl?: string
): string | undefined {
    const formConfig = getFormConfig(domainConfiguration, formKey);
    return getFormImageUrl(formConfig?.imageRef) || hallImageUrl || undefined;
}

/**
 * Resolves the custom body text configured for a form's own page, if any, so callers can use it
 * in place of their default translated copy. Markdown — render it through a markdown component,
 * not as plain text.
 *
 * Deliberately no fallback to the legacy `description`: that field fed the card and the page from
 * one input, so its content is a card blurb as often as page copy. Since a page text now replaces
 * the page's default sections wholesale, treating a legacy blurb as one would strip those sections
 * from every cinema that ever filled the old field. `resolveFormCardText` keeps reading it.
 */
export function resolveFormPageText(
    domainConfiguration: DomainConfigurationDto,
    formKey: FormContactKey
): string | undefined {
    return getFormConfig(domainConfiguration, formKey)?.pageText;
}

/**
 * Headline for a form's own page. Undefined when the operator has not set one, so callers keep
 * their translated default.
 */
export function resolveFormPageTitle(
    domainConfiguration: DomainConfigurationDto,
    formKey: FormContactKey
): string | undefined {
    return getFormConfig(domainConfiguration, formKey)?.pageTitle || undefined;
}

/**
 * Headline for the form's card on the home page.
 */
export function resolveFormCardTitle(
    domainConfiguration: DomainConfigurationDto,
    formKey: FormContactKey
): string | undefined {
    return getFormConfig(domainConfiguration, formKey)?.cardTitle || undefined;
}

/**
 * Body text for the form's card on the home page.
 *
 * Falls back to `description` when no card text is set: before card and page copy were split,
 * `description` was what the card displayed, so without this every cinema that had customised it
 * would silently drop back to the generic default the moment this shipped.
 */
export function resolveFormCardText(
    domainConfiguration: DomainConfigurationDto,
    formKey: FormContactKey
): string | undefined {
    const formConfig = getFormConfig(domainConfiguration, formKey);
    return formConfig?.cardText || formConfig?.description || undefined;
}
